/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Privacy Policy
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph align="center" sx={{ mb: 6 }}>
        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            1. Information We Collect
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1.1 Information You Provide
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Account information (email, password)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Debate content and messages" />
            </ListItem>
            <ListItem>
              <ListItemText primary="User-generated content and viewpoints" />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1.2 Information Automatically Collected
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Usage data and analytics" />
            </ListItem>
            <ListItem>
              <ListItemText primary="IP addresses and browser information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Cookies and similar tracking technologies" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            2. How We Use Your Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="To provide and maintain our service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To process your debates and messages" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To improve our platform and user experience" />
            </ListItem>
            <ListItem>
              <ListItemText primary="To communicate with you about your account or our service" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            3. Data Storage and Security
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="We implement appropriate security measures to protect your data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Data is stored on secure servers" />
            </ListItem>
            <ListItem>
              <ListItemText primary="We regularly review our security practices" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            4. Data Sharing
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="We do not sell your personal information" />
            </ListItem>
            <ListItem>
              <ListItemText primary="We may share data with service providers who assist in operating our platform" />
            </ListItem>
            <ListItem>
              <ListItemText primary="We may disclose information if required by law" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            5. Your Rights
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Access your personal data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Correct inaccurate data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Request deletion of your data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Opt-out of certain data collection" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            6. Cookies
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="We use cookies to improve your experience" />
            </ListItem>
            <ListItem>
              <ListItemText primary="You can control cookie settings through your browser" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Some features may not work without cookies" />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            7. Changes to This Policy
          </Typography>
          <Typography paragraph>
            We may update this policy periodically. We will notify you of any significant changes.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h5" gutterBottom>
            8. Contact Us
          </Typography>
          <Typography paragraph>
            For privacy-related questions, please contact: privacy@gauntletdebates.com
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage; 